import dot from 'dot-prop-immutable';
import { nanoid } from 'nanoid';
import { path, pickAll } from 'ramda';

import type { InterrogationStatus, ProcessingStatuses } from '@frontend/api';

import { INIT_EDIT_QUOTE, BAQ_PAGE_INIT_REQUEST } from 'store/quotes/types';
import type { ActionWithPayload } from 'store/types';

import * as types from './types';

export type ComponentInterrogationStatuses = {
  [componentId: string]: InterrogationStatus;
};

export type ComponentProcessingStatuses = {
  [componentId: string]: ProcessingStatuses;
};

export type QuoteItemsState = {
  areAddOnOverridesUpdating: Record<string, unknown>;
  areOverridesUpdating: Record<string, unknown>;
  overridesUpdatingQueue: unknown[];
  areSimilarComponentsSearching: Record<string, unknown>;
  areProfitItemOverridesUpdating: Record<string, unknown>;
  areDiscountOverridesUpdating: Record<string, unknown>;
  componentInterrogationStatuses: ComponentInterrogationStatuses;
  componentProcessingStatuses: ComponentProcessingStatuses;
  expandedQuoteItem: number | null;
  hasErrorByFieldName: Record<string, unknown>;
  isAddOnCreating: boolean;
  isAddingPurchasedComponent: boolean;
  isComponentUpdatePending: boolean;
  isFetchingByFieldName: Record<string, unknown>;
  isOperationCreatingByType: Record<string, unknown>;
  isOutsideServiceToggling: boolean;
  isCellUpdating: Record<string, unknown>;
  cellUpdatingQueue: Record<string, unknown>;
  componentsIdsToBomRollupRefresh: Record<string, boolean>;
  quoteItemMeta: Record<string, unknown>;
  quoteOpDefs: unknown[];
  quoteWorkflow: unknown | null;
  refreshingQuoteItemIds: unknown[];
  selectedQuoteItems: Record<string, unknown>;
  similarParts: Record<string, unknown>;
  isPartHistoryLoading: Record<string, unknown>;
  partHistory: Record<string, unknown>;
  snapToQuoteItemHeaderRequest: Record<string, unknown>;
  temporaryQuoteItemIds: unknown[];
  temporaryErpPartQuoteItemIds: unknown[];
  convertQuoteFileToQuoteItemTaskGroups: unknown[];
  updatingPartFilenames: Record<string, unknown>;
  updatingQuoteItemIds: unknown[];
  lockedQuoteItems: boolean;
};

const initialState: QuoteItemsState = {
  areAddOnOverridesUpdating: {},
  areOverridesUpdating: {},
  overridesUpdatingQueue: [],
  areSimilarComponentsSearching: {},
  areProfitItemOverridesUpdating: {},
  areDiscountOverridesUpdating: {},
  componentInterrogationStatuses: {},
  componentProcessingStatuses: {},
  expandedQuoteItem: null, // TODO: elevate this state
  hasErrorByFieldName: {},
  isAddOnCreating: false,
  isAddingPurchasedComponent: false,
  isComponentUpdatePending: false,
  isFetchingByFieldName: {},
  isOperationCreatingByType: {
    operation: false,
    material: false,
  },
  isOutsideServiceToggling: false,
  isCellUpdating: {},
  cellUpdatingQueue: {}, // queue of cell id by quantity id
  componentsIdsToBomRollupRefresh: {},
  quoteItemMeta: {},
  quoteOpDefs: [],
  quoteWorkflow: null,
  refreshingQuoteItemIds: [],
  selectedQuoteItems: {},
  similarParts: {},
  isPartHistoryLoading: {},
  partHistory: {},
  snapToQuoteItemHeaderRequest: {},
  temporaryQuoteItemIds: [],
  temporaryErpPartQuoteItemIds: [],
  convertQuoteFileToQuoteItemTaskGroups: [],
  updatingPartFilenames: {},
  updatingQuoteItemIds: [],
  lockedQuoteItems: false,
};

const setFields = <T>(
  state: QuoteItemsState,
  path: string,
  keys: string[],
  value: T,
) => {
  let nextState = state;
  for (const key of keys) {
    nextState = dot.set(nextState, `${path}.${key}`, value);
  }
  return nextState;
};

type QuoteItemsAction =
  | ActionWithPayload<typeof BAQ_PAGE_INIT_REQUEST>
  | ActionWithPayload<typeof types.QUOTE_ITEMS_RESET>
  | ActionWithPayload<typeof INIT_EDIT_QUOTE>
  | ActionWithPayload<typeof types.ADD_ON_CREATE_ERROR>
  | ActionWithPayload<typeof types.ADD_ON_CREATE_REQUEST>
  | ActionWithPayload<typeof types.ADD_ON_CREATE_SUCCESS>
  | ActionWithPayload<
      typeof types.UPDATE_ADD_ON_OVERRIDES_REQUEST,
      { addOnId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_ADD_ON_OVERRIDES_SUCCESS,
      { addOnId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_ADD_ON_OVERRIDES_ERROR,
      { addOnId: unknown }
    >
  | ActionWithPayload<
      typeof types.EXPAND_QUOTE_ITEM_REQUEST,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<typeof types.COLLAPSE_QUOTE_ITEM>
  | ActionWithPayload<typeof types.LOCK_QUOTE_ITEM>
  | ActionWithPayload<
      typeof types.EXPAND_QUOTE_ITEM_SUCCESS,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.EXPAND_QUOTE_ITEM_ERROR,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FETCH_INITIAL_BRIEF_COMPONENTS_REQUEST,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FETCH_INITIAL_BRIEF_COMPONENTS_SUCCESS,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FETCH_INITIAL_BRIEF_COMPONENTS_ERROR,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<typeof types.PURCHASED_COMPONENT_ADD_ERROR>
  | ActionWithPayload<typeof types.PURCHASED_COMPONENT_ADD_REQUEST>
  | ActionWithPayload<typeof types.PURCHASED_COMPONENT_ADD_SUCCESS>
  | ActionWithPayload<
      typeof types.RECEIVE_COMPONENT_INTERROGATION_STATUSES,
      { interrogationStatuses: ComponentInterrogationStatuses }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_COMPONENT_PROCESSING_STATUSES,
      { processingStatuses: ComponentProcessingStatuses }
    >
  | ActionWithPayload<
      typeof types.UPDATE_QUOTE_ITEM_REQUEST,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_COMPONENT_REQUEST,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_METADATA_SUCCESS,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_QUOTE_ITEM_SUCCESS,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_COMPONENT_SUCCESS,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_QUOTE_ITEM_ERROR,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<
      typeof types.UPDATE_COMPONENT_ERROR,
      { data: Record<string, unknown> }
    >
  | ActionWithPayload<typeof types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_REQUEST>
  | ActionWithPayload<typeof types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_ERROR>
  | ActionWithPayload<typeof types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_SUCCESS>
  | ActionWithPayload<
      typeof types.UPDATE_OPERATION_OVERRIDES_REQUEST,
      { operationId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_OPERATION_OVERRIDES_CANCEL,
      { operationId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_OPERATION_OVERRIDES_ERROR,
      { operationId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_OPERATION_SUCCESS,
      { operationId: unknown }
    >
  | ActionWithPayload<typeof types.RECEIVE_QUOTE_OP_DEFS, unknown[]>
  | ActionWithPayload<
      typeof types.RECEIVE_QUOTE_WORKFLOW,
      { quoteWorkflow: unknown }
    >
  | ActionWithPayload<
      typeof types.CREATE_OPERATION_REQUEST,
      { data: { category: unknown } }
    >
  | ActionWithPayload<
      typeof types.CREATE_OPERATION_ERROR,
      { category: unknown }
    >
  | ActionWithPayload<
      typeof types.CREATE_OPERATION_SUCCESS,
      { category: unknown }
    >
  | ActionWithPayload<typeof types.COMPONENT_UPDATE_PENDING_START>
  | ActionWithPayload<typeof types.COMPONENT_UPDATE_PENDING_FINISH>
  | ActionWithPayload<
      typeof types.FORCE_QUOTE_ITEM_RECALCULATION_REQUEST,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FORCE_COMPONENT_REGENERATION_REQUEST,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FORCE_QUOTE_ITEM_RECALCULATION_ERROR,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FORCE_QUOTE_ITEM_RECALCULATION_SUCCESS,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FORCE_COMPONENT_REGENERATION_SUCCESS,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.FORCE_COMPONENT_REGENERATION_ERROR,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.SET_QUOTE_ITEMS_UPDATING_REQUEST,
      { quoteItemIds: unknown[] }
    >
  | ActionWithPayload<
      typeof types.SET_QUOTE_ITEMS_UPDATING_ERROR,
      { quoteItemIds: unknown[] }
    >
  | ActionWithPayload<
      typeof types.SET_QUOTE_ITEMS_UPDATING_SUCCESS,
      { quoteItemIds: unknown[] }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_TEMPORARY_QUOTE_ITEM_IDS,
      { temporaryQuoteItemIds: unknown[] }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEM,
      { taskId: unknown }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEMS,
      { temporaryErpPartQuoteItemIds: unknown[] }
    >
  | ActionWithPayload<
      typeof types.ADD_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS,
      { tasks: unknown }
    >
  | ActionWithPayload<
      typeof types.REMOVE_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS,
      { tasks: unknown[] }
    >
  | ActionWithPayload<typeof types.CLEAR_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS>
  | ActionWithPayload<
      typeof types.SNAP_TO_QUOTE_ITEM_HEADER_REQUEST,
      { quoteItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_PART_FILENAME_ERROR,
      { componentId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_PART_FILENAME_SUCCESS,
      { componentId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_PART_FILENAME_REQUEST,
      { componentId: unknown }
    >
  | ActionWithPayload<
      typeof types.REFRESH_BOM_ROLLUP_REQUEST,
      { parentComponentIds: number[] }
    >
  | ActionWithPayload<typeof types.REFRESH_BOM_ROLLUP_FINISHED>
  | ActionWithPayload<typeof types.PROFIT_ITEM_CREATE_ERROR>
  | ActionWithPayload<typeof types.PROFIT_ITEM_CREATE_REQUEST>
  | ActionWithPayload<typeof types.PROFIT_ITEM_CREATE_SUCCESS>
  | ActionWithPayload<
      typeof types.UPDATE_PROFIT_ITEM_OVERRIDES_REQUEST,
      { profitItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_PROFIT_ITEM_OVERRIDES_SUCCESS,
      { profitItemId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_PROFIT_ITEM_OVERRIDES_ERROR,
      { profitItemId: unknown }
    >
  | ActionWithPayload<typeof types.DISCOUNT_CREATE_ERROR>
  | ActionWithPayload<typeof types.DISCOUNT_CREATE_REQUEST>
  | ActionWithPayload<typeof types.DISCOUNT_CREATE_SUCCESS>
  | ActionWithPayload<
      typeof types.UPDATE_DISCOUNT_OVERRIDES_REQUEST,
      { discountId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_DISCOUNT_OVERRIDES_SUCCESS,
      { discountId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_DISCOUNT_OVERRIDES_ERROR,
      { discountId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_CELL_REQUEST,
      { cellId: unknown; quantityId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_CELL_SUCCESS,
      { cellId: unknown; quantityId: unknown }
    >
  | ActionWithPayload<
      typeof types.UPDATE_CELL_ERROR,
      { cellId: unknown; quantityId: unknown }
    >
  | ActionWithPayload<
      typeof types.BRIEF_COMPONENT_RECEIVE,
      { entities?: { quoteItemBriefComponents?: Record<string, unknown> } }
    >
  | ActionWithPayload<
      typeof types.BRIEF_COMPONENT_RECEIVE_MULTIPLE,
      { entities?: { quoteItemBriefComponents?: Record<string, unknown> } }
    >;

const reducer = (state = initialState, action: QuoteItemsAction) => {
  switch (action.type) {
    case BAQ_PAGE_INIT_REQUEST:
    case types.QUOTE_ITEMS_RESET:
      return initialState;
    case INIT_EDIT_QUOTE:
      return {
        ...state,
        ...pickAll(
          ['selectedQuoteItems', 'temporaryQuoteItemIds'],
          initialState,
        ),
      };
    case types.ADD_ON_CREATE_ERROR:
      return {
        ...state,
        isAddOnCreating: false,
      };
    case types.ADD_ON_CREATE_REQUEST:
      return {
        ...state,
        isAddOnCreating: true,
      };
    case types.ADD_ON_CREATE_SUCCESS:
      return {
        ...state,
        isAddOnCreating: false,
      };
    case types.UPDATE_ADD_ON_OVERRIDES_REQUEST:
      return dot.set(
        state,
        `areAddOnOverridesUpdating.${action.payload.addOnId}`,
        true,
      );
    case types.UPDATE_ADD_ON_OVERRIDES_SUCCESS:
    case types.UPDATE_ADD_ON_OVERRIDES_ERROR:
      return dot.set(
        state,
        `areAddOnOverridesUpdating.${action.payload.addOnId}`,
        false,
      );
    case types.EXPAND_QUOTE_ITEM_REQUEST:
      return dot.set(
        {
          ...state,
          expandedQuoteItem: action.payload.quoteItemId,
          isExpandedQuoteItemLocked: false,
        },
        `quoteItemMeta.${action.payload.quoteItemId}.isFetching`,
        true,
      );
    case types.COLLAPSE_QUOTE_ITEM:
      return {
        ...state,
        expandedQuoteItem: null,
      };
    case types.LOCK_QUOTE_ITEM:
      return {
        ...state,
        isExpandedQuoteItemLocked: true,
      };
    case types.EXPAND_QUOTE_ITEM_SUCCESS:
      return {
        ...dot.set(
          state,
          `quoteItemMeta.${action.payload.quoteItemId}.isFetching`,
          false,
        ),
      };
    case types.EXPAND_QUOTE_ITEM_ERROR:
      return dot.set(
        state,
        `quoteItemMeta.${action.payload.quoteItemId}.isFetching`,
        false,
      );
    case types.FETCH_INITIAL_BRIEF_COMPONENTS_REQUEST:
      return dot.set(
        state,
        `quoteItemMeta.${action.payload.quoteItemId}.isFetchingBriefComponents`,
        true,
      );
    case types.FETCH_INITIAL_BRIEF_COMPONENTS_SUCCESS:
      return dot.set(
        state,
        `quoteItemMeta.${action.payload.quoteItemId}.isFetchingBriefComponents`,
        false,
      );
    case types.FETCH_INITIAL_BRIEF_COMPONENTS_ERROR:
      return dot.set(
        state,
        `quoteItemMeta.${action.payload.quoteItemId}.isFetchingBriefComponents`,
        false,
      );
    case types.PURCHASED_COMPONENT_ADD_ERROR:
      return {
        ...state,
        isAddingPurchasedComponent: false,
      };
    case types.PURCHASED_COMPONENT_ADD_REQUEST:
      return {
        ...state,
        isAddingPurchasedComponent: true,
      };
    case types.PURCHASED_COMPONENT_ADD_SUCCESS:
      return {
        ...state,
        isAddingPurchasedComponent: false,
      };
    case types.RECEIVE_COMPONENT_INTERROGATION_STATUSES:
      return dot.merge(
        state,
        'componentInterrogationStatuses',
        action.payload.interrogationStatuses,
      );
    case types.RECEIVE_COMPONENT_PROCESSING_STATUSES:
      return dot.merge(
        state,
        'componentProcessingStatuses',
        action.payload.processingStatuses,
      );
    case types.UPDATE_QUOTE_ITEM_REQUEST:
    case types.UPDATE_COMPONENT_REQUEST: {
      const nextState = setFields(
        state,
        'isFetchingByFieldName',
        Object.keys(action.payload.data),
        true,
      );
      return setFields(
        nextState,
        'hasErrorByFieldName',
        Object.keys(action.payload.data),
        false,
      );
    }
    case types.UPDATE_METADATA_SUCCESS:
    case types.UPDATE_QUOTE_ITEM_SUCCESS:
    case types.UPDATE_COMPONENT_SUCCESS:
      return setFields(
        state,
        'isFetchingByFieldName',
        Object.keys(action.payload.data),
        false,
      );
    case types.UPDATE_QUOTE_ITEM_ERROR:
    case types.UPDATE_COMPONENT_ERROR: {
      const nextState = setFields(
        state,
        'isFetchingByFieldName',
        Object.keys(action.payload.data),
        false,
      );
      return setFields(
        nextState,
        'hasErrorByFieldName',
        Object.keys(action.payload.data),
        true,
      );
    }
    case types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_REQUEST:
      return {
        ...state,
        isOutsideServiceToggling: true,
      };
    case types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_ERROR:
    case types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_SUCCESS:
      return {
        ...state,
        isOutsideServiceToggling: false,
      };
    case types.UPDATE_OPERATION_OVERRIDES_REQUEST:
      return {
        ...state,
        areOverridesUpdating: dot.set(
          state.areOverridesUpdating,
          `${action.payload.operationId}`,
          true,
        ),
        overridesUpdatingQueue: state.overridesUpdatingQueue.concat([
          action.payload.operationId,
        ]),
      };
    case types.UPDATE_OPERATION_OVERRIDES_CANCEL: {
      const nextOverridesUpdatingQueue = [...state.overridesUpdatingQueue];
      const index = nextOverridesUpdatingQueue.indexOf(
        action.payload.operationId,
      );
      if (index > -1) {
        nextOverridesUpdatingQueue.splice(index, 1);
      }
      return {
        ...state,
        overridesUpdatingQueue: nextOverridesUpdatingQueue,
      };
    }
    case types.UPDATE_OPERATION_OVERRIDES_ERROR:
    case types.UPDATE_OPERATION_SUCCESS: {
      const nextOverridesUpdatingQueue = [...state.overridesUpdatingQueue];
      const index = nextOverridesUpdatingQueue.indexOf(
        action.payload.operationId,
      );
      if (index > -1) {
        nextOverridesUpdatingQueue.splice(index, 1);
      }
      return {
        ...state,
        areOverridesUpdating: dot.set(
          state.areOverridesUpdating,
          `${action.payload.operationId}`,
          false,
        ),
        overridesUpdatingQueue: nextOverridesUpdatingQueue,
      };
    }
    case types.RECEIVE_QUOTE_OP_DEFS:
      return {
        ...state,
        quoteOpDefs: action.payload,
      };
    case types.RECEIVE_QUOTE_WORKFLOW:
      return {
        ...state,
        quoteWorkflow: action.payload.quoteWorkflow,
      };
    case types.CREATE_OPERATION_REQUEST:
      return dot.set(
        state,
        `isOperationCreatingByType.${path(
          ['data', 'category'],
          action.payload,
        )}`,
        true,
      );
    case types.CREATE_OPERATION_ERROR:
    case types.CREATE_OPERATION_SUCCESS:
      return dot.set(
        state,
        `isOperationCreatingByType.${path(['category'], action.payload)}`,
        false,
      );
    case types.COMPONENT_UPDATE_PENDING_START:
      return {
        ...state,
        isComponentUpdatePending: true,
      };
    case types.COMPONENT_UPDATE_PENDING_FINISH:
      return {
        ...state,
        isComponentUpdatePending: false,
      };
    case types.FORCE_QUOTE_ITEM_RECALCULATION_REQUEST:
    case types.FORCE_COMPONENT_REGENERATION_REQUEST:
      return {
        ...state,
        refreshingQuoteItemIds: state.refreshingQuoteItemIds.concat([
          action.payload.quoteItemId,
        ]),
      };
    case types.FORCE_QUOTE_ITEM_RECALCULATION_ERROR:
    case types.FORCE_QUOTE_ITEM_RECALCULATION_SUCCESS:
    case types.FORCE_COMPONENT_REGENERATION_SUCCESS:
    case types.FORCE_COMPONENT_REGENERATION_ERROR:
      return {
        ...state,
        refreshingQuoteItemIds: state.refreshingQuoteItemIds.filter(
          (id) => id !== action.payload.quoteItemId,
        ),
      };
    case types.SET_QUOTE_ITEMS_UPDATING_REQUEST:
      return {
        ...state,
        updatingQuoteItemIds: [
          ...state.updatingQuoteItemIds,
          ...action.payload.quoteItemIds,
        ],
      };
    case types.SET_QUOTE_ITEMS_UPDATING_ERROR:
    case types.SET_QUOTE_ITEMS_UPDATING_SUCCESS:
      return {
        ...state,
        updatingQuoteItemIds: state.updatingQuoteItemIds.filter(
          (id) => !action.payload.quoteItemIds.includes(id),
        ),
      };
    case types.RECEIVE_TEMPORARY_QUOTE_ITEM_IDS:
      return {
        ...state,
        temporaryQuoteItemIds: action.payload.temporaryQuoteItemIds,
      };
    case types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEM:
      return {
        ...state,
        temporaryErpPartQuoteItemIds: [
          ...state.temporaryErpPartQuoteItemIds,
          action.payload.taskId,
        ],
      };
    case types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEMS:
      return {
        ...state,
        temporaryErpPartQuoteItemIds:
          action.payload.temporaryErpPartQuoteItemIds,
      };
    case types.ADD_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS: {
      return {
        ...state,
        convertQuoteFileToQuoteItemTaskGroups: [
          ...state.convertQuoteFileToQuoteItemTaskGroups,
          action.payload.tasks,
        ],
      };
    }
    case types.REMOVE_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS: {
      const tasksToRemove = action.payload.tasks;
      const newGroups = state.convertQuoteFileToQuoteItemTaskGroups
        .map((taskGroup) =>
          taskGroup.filter((task) => !tasksToRemove.includes(task.taskId)),
        )
        .filter((taskGroup) => taskGroup.length > 0);
      return {
        ...state,
        convertQuoteFileToQuoteItemTaskGroups: newGroups,
      };
    }
    case types.CLEAR_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS: {
      return {
        ...state,
        convertQuoteFileToQuoteItemTaskGroups: [],
      };
    }
    case types.SNAP_TO_QUOTE_ITEM_HEADER_REQUEST:
      return dot.set(
        state,
        `snapToQuoteItemHeaderRequest.${action.payload.quoteItemId}`,
        nanoid(),
      );
    case types.UPDATE_PART_FILENAME_ERROR:
    case types.UPDATE_PART_FILENAME_SUCCESS:
      return dot.set(
        state,
        `updatingPartFilenames.${action.payload.componentId}`,
        false,
      );
    case types.UPDATE_PART_FILENAME_REQUEST:
      return dot.set(
        state,
        `updatingPartFilenames.${action.payload.componentId}`,
        true,
      );
    case types.REFRESH_BOM_ROLLUP_REQUEST: {
      const toMerge: Record<string, boolean> = {};
      for (const compId of action.payload.parentComponentIds) {
        toMerge[compId] = true;
      }
      return dot.merge(state, 'componentsIdsToBomRollupRefresh', toMerge);
    }
    case types.REFRESH_BOM_ROLLUP_FINISHED:
      return {
        ...state,
        componentsIdsToBomRollupRefresh: {},
      };
    case types.PROFIT_ITEM_CREATE_ERROR:
      return {
        ...state,
        isProfitItemCreating: false,
      };
    case types.PROFIT_ITEM_CREATE_REQUEST:
      return {
        ...state,
        isProfitItemCreating: true,
      };
    case types.PROFIT_ITEM_CREATE_SUCCESS:
      return {
        ...state,
        isProfitItemCreating: false,
      };
    case types.UPDATE_PROFIT_ITEM_OVERRIDES_REQUEST:
      return dot.set(
        state,
        `areProfitItemOverridesUpdating.${action.payload.profitItemId}`,
        true,
      );
    case types.UPDATE_PROFIT_ITEM_OVERRIDES_SUCCESS:
    case types.UPDATE_PROFIT_ITEM_OVERRIDES_ERROR:
      return dot.set(
        state,
        `areProfitItemOverridesUpdating.${action.payload.profitItemId}`,
        false,
      );
    case types.DISCOUNT_CREATE_ERROR:
      return {
        ...state,
        isDiscountCreating: false,
      };
    case types.DISCOUNT_CREATE_REQUEST:
      return {
        ...state,
        isDiscountCreating: true,
      };
    case types.DISCOUNT_CREATE_SUCCESS:
      return {
        ...state,
        isDiscountCreating: false,
      };
    case types.UPDATE_DISCOUNT_OVERRIDES_REQUEST:
      return dot.set(
        state,
        `areDiscountOverridesUpdating.${action.payload.discountId}`,
        true,
      );
    case types.UPDATE_DISCOUNT_OVERRIDES_SUCCESS:
    case types.UPDATE_DISCOUNT_OVERRIDES_ERROR:
      return dot.set(
        state,
        `areDiscountOverridesUpdating.${action.payload.discountId}`,
        false,
      );
    case types.UPDATE_CELL_REQUEST: {
      const quantityQueue =
        state.cellUpdatingQueue[action.payload.quantityId] ?? [];
      return {
        ...state,
        isCellUpdating: {
          ...state.isCellUpdating,
          [action.payload.cellId]: true,
        },
        cellUpdatingQueue: {
          ...state.cellUpdatingQueue,
          [action.payload.quantityId]: quantityQueue.concat([
            action.payload.cellId,
          ]),
        },
      };
    }
    case types.UPDATE_CELL_SUCCESS:
    case types.UPDATE_CELL_ERROR: {
      const quantityQueue =
        state.cellUpdatingQueue[action.payload.quantityId] ?? [];
      const index = quantityQueue.indexOf(action.payload.cellId);
      const nextQuantityQueue = [...quantityQueue];
      if (index > -1) {
        nextQuantityQueue.splice(index, 1);
      }
      const nextCellUpdatingQueue = {
        ...state.cellUpdatingQueue,
        [action.payload.quantityId]: nextQuantityQueue,
      };
      // delete the queue if empty to reduce amount of keys to iterate through
      // in selectors
      if (nextQuantityQueue.length === 0) {
        delete nextCellUpdatingQueue[action.payload.quantityId];
      }
      return {
        ...state,
        isCellUpdating: {
          ...state.isCellUpdating,
          [action.payload.cellId]: false,
        },
        cellUpdatingQueue: nextCellUpdatingQueue,
      };
    }
    case types.BRIEF_COMPONENT_RECEIVE:
    case types.BRIEF_COMPONENT_RECEIVE_MULTIPLE: {
      const quoteItemBriefComponents =
        action.payload?.entities?.quoteItemBriefComponents;

      const nextProcessingStatuses: Record<string, ProcessingStatus> = {};
      const nextInterrogationStatuses: Record<string, InterrogationStatus> = {};
      if (quoteItemBriefComponents) {
        for (const [componentId, briefComponent] of Object.entries(
          quoteItemBriefComponents,
        )) {
          const { status } = briefComponent;
          nextProcessingStatuses[componentId.toString()] = status?.processing;
          nextInterrogationStatuses[componentId.toString()] =
            status?.interrogation;
        }
      }
      return dot.merge(
        dot.merge(state, 'componentProcessingStatuses', nextProcessingStatuses),
        'componentInterrogationStatuses',
        nextInterrogationStatuses,
      );
    }
    default:
      return state;
  }
};

export default reducer;
